import React, { useState } from 'react';
import { 
  Box, 
  Container, 
  Typography, 
  Button, 
  Grid, 
  Card, 
  CardContent, 
  useTheme, 
  useMediaQuery, 
  Menu,
  MenuItem,
  IconButton,
  Divider,
  Stack,
  Link,
} from '@mui/material';
import { motion } from 'framer-motion';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { 
  School, 
  ArrowForward, 
  DeveloperBoard, 
  Menu as MenuIcon, 
  Close as CloseIcon, 
  Science, 
  MenuBook, 
  Psychology, 
  Analytics, 
  Security, 
  GroupWork, 
  CheckCircleOutline, 
  Bolt, 
  KeyboardArrowDown,
  GitHub,
  LinkedIn,
  Twitter,
  LocalHospital,
  AttachMoney
} from '@mui/icons-material';
import { useAuthModal } from '../contexts/AuthModalContext';

const AnimatedBox = motion(Box);

const Landing: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const { openLogin } = useAuthModal();

  // State for Login dropdown menu (Header)
  const [loginAnchorEl, setLoginAnchorEl] = useState<null | HTMLElement>(null);
  const isLoginMenuOpen = Boolean(loginAnchorEl);
  
  // State for Signup dropdown menu (Header)
  const [signupAnchorEl, setSignupAnchorEl] = useState<null | HTMLElement>(null);
  const isSignupMenuOpen = Boolean(signupAnchorEl);

  // State for Mobile navigation drawer overlay
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Interactive statistics state preview
  const [activeTab, setActiveTab] = useState<'academy' | 'research'>('academy');

  const handleLoginClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setLoginAnchorEl(event.currentTarget);
  };

  const handleSignupClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setSignupAnchorEl(event.currentTarget);
  };

  const handleLoginClose = () => {
    setLoginAnchorEl(null);
  };

  const handleSignupClose = () => {
    setSignupAnchorEl(null);
  };

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setMobileMenuOpen(false);
  };

  const subsidiaryHighlights = [
    {
      title: 'EduTech',
      accent: '#6366f1',
      icon: <School sx={{ fontSize: 28, color: '#fff' }} />,
      summary: 'Built for learners, parents, and schools.',
      description: 'A3 EduTech delivers WAEC and JAMB preparation, virtual science labs, live mock exams, parent dashboards, and structured learning pathways that help students stay on track and improve performance.',
      bullets: ['WAEC/JAMB exam simulators', 'Virtual STEM lab experiences', 'School analytics and progress tracking']
    },
    {
      title: 'MedTech',
      accent: '#14b8a6',
      icon: <LocalHospital sx={{ fontSize: 28, color: '#fff' }} />,
      summary: 'Digital tools for healthcare learning and access.',
      description: 'A3 MedTech supports healthcare education, clinical research workflows, medical knowledge delivery, and patient-friendly digital experiences that make complex care information easier to understand and apply.',
      bullets: ['Clinical and research knowledge hubs', 'Healthcare education content', 'Patient-centered digital support tools']
    },
    {
      title: 'FinTech',
      accent: '#f59e0b',
      icon: <AttachMoney sx={{ fontSize: 28, color: '#fff' }} />,
      summary: 'Financial systems for learning, growth, and trust.',
      description: 'A3 FinTech brings together financial literacy, budgeting tools, secure payment workflows, subscription systems, and intelligent reporting for schools, families, and digital service users.',
      bullets: ['Secure payments and subscriptions', 'Financial literacy tools', 'Performance and revenue reporting']
    }
  ];

  return (
    <Box sx={{ bgcolor: '#0b0f19', color: '#ffffff', minHeight: '100vh', overflowX: 'hidden' }}>
      
      {/* 1. DEDICATED HEADER SECTION */}
      <Box 
        component="header"
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 1000,
          background: 'rgba(11, 15, 25, 0.75)',
          backdropFilter: 'blur(20px)',
          borderBottom: '1px solid rgba(255, 255, 255, 0.06)',
          transition: 'all 0.3s ease',
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: '76px' }}>
            
            {/* Logo and Brand */}
            <Box 
              sx={{ display: 'flex', alignItems: 'center', gap: 1.5, cursor: 'pointer' }}
              onClick={() => navigate('/')}
            >
              <Box component="img" src={`${process.env.PUBLIC_URL}/logo_with_bottom_text.svg`} alt="A3Tech Education" sx={{ width: 84, height: 56, objectFit: 'contain' }} />
              <Typography 
                variant="h6" 
                sx={{ 
                  fontWeight: 800, 
                  letterSpacing: '-0.02em',
                  background: 'linear-gradient(to right, #ffffff, #c7d2fe)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent'
                }}
              >
                A3 Tech
              </Typography>
            </Box>

            {/* Desktop Navigation Links */}
            {!isMobile && (
              <Box sx={{ display: 'flex', gap: 4 }}>
                {['Portals', 'Features', 'Insights', 'Workflow'].map((link) => (
                  <Typography 
                    key={link}
                    onClick={() => scrollToSection(link.toLowerCase().replace(' ', '-'))}
                    sx={{ 
                      fontSize: '0.92rem', 
                      fontWeight: 500, 
                      color: '#94a3b8', 
                      cursor: 'pointer',
                      transition: 'color 0.2s',
                      '&:hover': { color: '#ffffff' }
                    }}
                  >
                    {link}
                  </Typography>
                ))}
              </Box>
            )}

            {/* Desktop Action Buttons */}
            {!isMobile ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                {/* Login Dropdown Toggle */}
                <Button
                  variant="text"
                  endIcon={<KeyboardArrowDown />}
                  onClick={handleLoginClick}
                  sx={{
                    color: '#e2e8f0',
                    textTransform: 'none',
                    fontWeight: 600,
                    px: 2,
                    py: 1,
                    borderRadius: '8px',
                    '&:hover': { background: 'rgba(255,255,255,0.04)' }
                  }}
                >
                  Login
                </Button>

                {/* Sign Up Dropdown Toggle */}
                <Button
                  variant="contained"
                  onClick={handleSignupClick}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 600,
                    px: 2.5,
                    py: 1.1,
                    borderRadius: '10px',
                    background: 'linear-gradient(135deg, #4f46e5 0%, #3730a3 100%)',
                    boxShadow: '0 4px 12px rgba(79, 70, 229, 0.25)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #4338ca 0%, #312e81 100%)',
                      boxShadow: '0 6px 16px rgba(79, 70, 229, 0.4)'
                    }
                  }}
                >
                  Get Started
                </Button>
              </Box>
            ) : (
              // Mobile Hamburger Button
              <IconButton 
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                sx={{ color: '#ffffff', background: 'rgba(255,255,255,0.03)' }}
              >
                {mobileMenuOpen ? <CloseIcon /> : <MenuIcon />}
              </IconButton>
            )}

          </Box>
        </Container>
      </Box>

      {/* Mobile Drawer Overlay */}
      {mobileMenuOpen && (
        <Box 
          component={motion.div}
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.2 }}
          sx={{
            position: 'fixed',
            top: '76px',
            left: 0,
            right: 0,
            bgcolor: '#0f172a',
            borderBottom: '1px solid rgba(255,255,255,0.08)',
            zIndex: 999,
            py: 3,
            px: 4,
            boxShadow: '0 20px 30px rgba(0,0,0,0.5)',
            display: 'flex',
            flexDirection: 'column',
            gap: 3
          }}
        >
          {['Portals', 'Features', 'Insights', 'Workflow'].map((link) => (
            <Typography 
              key={link}
              onClick={() => scrollToSection(link.toLowerCase().replace(' ', '-'))}
              sx={{ fontSize: '1.1rem', fontWeight: 600, color: '#94a3b8', cursor: 'pointer' }}
            >
              {link}
            </Typography>
          ))}
          <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)' }} />
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Button 
              variant="outlined" 
              fullWidth
              onClick={() => { setMobileMenuOpen(false); navigate('/high-school/login'); }}
              sx={{ py: 1.5, textTransform: 'none', borderRadius: '10px', color: '#e2e8f0', borderColor: 'rgba(255,255,255,0.15)' }}
            >
              High School Portal
            </Button>
            <Button 
              variant="contained" 
              fullWidth
              onClick={() => { setMobileMenuOpen(false); openLogin(); }}
              sx={{ py: 1.5, textTransform: 'none', borderRadius: '10px', background: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)' }}
            >
              Project & Research Portal
            </Button>
          </Box>
        </Box>
      )}

      {/* Header Menus (Desktop) */}
      <Menu
        anchorEl={loginAnchorEl}
        open={isLoginMenuOpen}
        onClose={handleLoginClose}
        PaperProps={{
          sx: {
            bgcolor: '#0f172a',
            border: '1px solid rgba(255,255,255,0.08)',
            borderRadius: '12px',
            mt: 1.5,
            boxShadow: '0 10px 25px rgba(0,0,0,0.4)',
            color: '#ffffff',
            minWidth: '220px',
          }
        }}
      >
        <MenuItem 
          onClick={() => { handleLoginClose(); navigate('/high-school/login'); }}
          sx={{ py: 1.5, px: 2.5, '&:hover': { bgcolor: 'rgba(255,255,255,0.04)' } }}
        >
          <School sx={{ mr: 2, color: '#6366f1', fontSize: 20 }} />
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>High School Login</Typography>
            <Typography variant="caption" sx={{ color: '#94a3b8' }}>WAEC / JAMB prep</Typography>
          </Box>
        </MenuItem>
        <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)' }} />
        <MenuItem 
          onClick={() => { handleLoginClose(); openLogin(); }}
          sx={{ py: 1.5, px: 2.5, '&:hover': { bgcolor: 'rgba(255,255,255,0.04)' } }}
        >
          <DeveloperBoard sx={{ mr: 2, color: '#ec4899', fontSize: 20 }} />
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>Project Portal Login</Typography>
            <Typography variant="caption" sx={{ color: '#94a3b8' }}>Research & Engineering</Typography>
          </Box>
        </MenuItem>
      </Menu>

      <Menu
        anchorEl={signupAnchorEl}
        open={isSignupMenuOpen}
        onClose={handleSignupClose}
        PaperProps={{
          sx: {
            bgcolor: '#0f172a',
            border: '1px solid rgba(255,255,255,0.08)',
            borderRadius: '12px',
            mt: 1.5,
            boxShadow: '0 10px 25px rgba(0,0,0,0.4)',
            color: '#ffffff',
            minWidth: '220px',
          }
        }}
      >
        <MenuItem 
          onClick={() => { handleSignupClose(); navigate('/high-school/signup'); }}
          sx={{ py: 1.5, px: 2.5, '&:hover': { bgcolor: 'rgba(255,255,255,0.04)' } }}
        >
          <School sx={{ mr: 2, color: '#6366f1', fontSize: 20 }} />
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>High School Sign Up</Typography>
            <Typography variant="caption" sx={{ color: '#94a3b8' }}>Register as a student</Typography>
          </Box>
        </MenuItem>
        <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)' }} />
        <MenuItem 
          onClick={() => { handleSignupClose(); openLogin(); }} // Triggers the unified auth modal
          sx={{ py: 1.5, px: 2.5, '&:hover': { bgcolor: 'rgba(255,255,255,0.04)' } }}
        >
          <DeveloperBoard sx={{ mr: 2, color: '#ec4899', fontSize: 20 }} />
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>Project & Research Access</Typography>
            <Typography variant="caption" sx={{ color: '#94a3b8' }}>Request developer access</Typography>
          </Box>
        </MenuItem>
      </Menu>


      {/* 2. HERO & PORTALS GATEWAY SECTION */}
      <Box 
        id="portals"
        sx={{ 
          pt: 18,
          pb: 12,
          position: 'relative',
          background: 'radial-gradient(circle at 50% 15%, #1e1b4b 0%, #0b0f19 80%)',
        }}
      >
        {/* Animated Background Lights */}
        <Box 
          component={motion.div}
          animate={{ opacity: [0.2, 0.4, 0.2], scale: [1, 1.1, 1] }}
          transition={{ duration: 8, repeat: Infinity }}
          sx={{
            position: 'absolute',
            top: '5%',
            left: '20%',
            width: '450px',
            height: '450px',
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(99, 102, 241, 0.15) 0%, transparent 65%)',
            filter: 'blur(60px)',
            pointerEvents: 'none'
          }}
        />
        <Box 
          component={motion.div}
          animate={{ opacity: [0.15, 0.3, 0.15], scale: [1.1, 1, 1.1] }}
          transition={{ duration: 10, repeat: Infinity }}
          sx={{
            position: 'absolute',
            top: '15%',
            right: '20%',
            width: '450px',
            height: '450px',
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(217, 70, 239, 0.1) 0%, transparent 65%)',
            filter: 'blur(70px)',
            pointerEvents: 'none'
          }}
        />

        <Container maxWidth="lg">
          {/* Main Hero Header */}
          <Box sx={{ textAlign: 'center', mb: 9 }}>
            <Box 
              sx={{ 
                display: 'inline-flex', 
                alignItems: 'center', 
                gap: 1, 
                px: 2, 
                py: 0.8, 
                borderRadius: '20px', 
                bgcolor: 'rgba(99, 102, 241, 0.15)',
                border: '1px solid rgba(99, 102, 241, 0.25)',
                mb: 3
              }}
            >
              <Bolt sx={{ color: '#818cf8', fontSize: 16 }} />
              <Typography variant="caption" sx={{ color: '#a5b4fc', fontWeight: 600, letterSpacing: '0.05em' }}>
                REENGINEERING MODERN ACADEMICS & RESEARCH
              </Typography>
            </Box>
            
            <Typography
              component={motion.h1}
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              variant={isMobile ? 'h3' : 'h2'}
              sx={{
                fontWeight: 900,
                letterSpacing: '-0.025em',
                lineHeight: 1.1,
                mb: 3,
                background: 'linear-gradient(to right, #ffffff 30%, #e2e8f0 60%, #cbd5e1 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent'
              }}
            >
              Empowering Minds,<br />Accelerating Engineering.
            </Typography>

            <Typography
              component={motion.p}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              variant="h6"
              sx={{
                color: '#94a3b8',
                maxWidth: '750px',
                mx: 'auto',
                fontWeight: 400,
                lineHeight: 1.6,
                fontSize: { xs: '1rem', md: '1.15rem' }
              }}
            >
              A3 Tech is a dual-pillar platform offering structured national exam simulators for high school students alongside advanced research databases and documentation tools for modern creators.
            </Typography>
          </Box>

          {/* Portals Cards Row */}
          <Grid container spacing={5} justifyContent="center">
            
            {/* 1. High School Academy Card */}
            <Grid item xs={12} md={6}>
              <AnimatedBox
                initial={{ opacity: 0, x: -30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.7, delay: 0.3 }}
                sx={{ height: '100%' }}
              >
                <Card
                  sx={{
                    height: '100%',
                    background: 'rgba(23, 28, 41, 0.45)',
                    backdropFilter: 'blur(20px)',
                    border: '1px solid rgba(99, 102, 241, 0.15)',
                    borderRadius: '24px',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    p: 2.5,
                    boxShadow: '0 20px 40px rgba(0,0,0,0.4)',
                    transition: 'all 0.4s cubic-bezier(0.16, 1, 0.3, 1)',
                    position: 'relative',
                    overflow: 'hidden',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '3px',
                      background: 'linear-gradient(90deg, #6366f1, #4f46e5)',
                    },
                    '&:hover': {
                      transform: 'translateY(-6px)',
                      borderColor: 'rgba(99, 102, 241, 0.4)',
                      boxShadow: '0 25px 50px rgba(99, 102, 241, 0.15)'
                    }
                  }}
                >
                  <CardContent sx={{ p: 3.5 }}>
                    <Box 
                      sx={{ 
                        width: 54, 
                        height: 54, 
                        borderRadius: '14px', 
                        background: 'linear-gradient(135deg, #6366f1 0%, #4338ca 100%)',
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center', 
                        mb: 3,
                        boxShadow: '0 8px 16px rgba(99,102,241,0.3)'
                      }}
                    >
                      <School sx={{ fontSize: 26, color: '#ffffff' }} />
                    </Box>

                    <Typography variant="h4" sx={{ fontWeight: 800, mb: 1, letterSpacing: '-0.01em' }}>
                      High School Academy
                    </Typography>
                    <Typography variant="subtitle2" sx={{ color: '#a5b4fc', fontWeight: 600, mb: 3, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                      WAEC & JAMB Preparation Hub
                    </Typography>

                    <Typography variant="body2" sx={{ color: '#94a3b8', mb: 4, lineHeight: 1.7, fontSize: '0.95rem' }}>
                      A comprehensive academic workspace providing high-fidelity virtual science labs, structured course outlines, exam mock environments, instant AI-assisted diagnostics, and tracking tools for parents.
                    </Typography>

                    <Stack spacing={2.5}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <CheckCircleOutline sx={{ color: '#818cf8', fontSize: 20 }} />
                        <Typography variant="body2" sx={{ color: '#cbd5e1', fontWeight: 500 }}>High-Fidelity Virtual Physics & Chem Labs</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <CheckCircleOutline sx={{ color: '#818cf8', fontSize: 20 }} />
                        <Typography variant="body2" sx={{ color: '#cbd5e1', fontWeight: 500 }}>WAEC / JAMB Simulated Exam Engine</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <CheckCircleOutline sx={{ color: '#818cf8', fontSize: 20 }} />
                        <Typography variant="body2" sx={{ color: '#cbd5e1', fontWeight: 500 }}>Real-time parent monitoring dashboards</Typography>
                      </Box>
                    </Stack>
                  </CardContent>

                  <Box sx={{ px: 3.5, pb: 3.5, pt: 1.5 }}>
                    <Button
                      variant="contained"
                      fullWidth
                      size="large"
                      endIcon={<ArrowForward />}
                      onClick={() => navigate('/high-school/login')}
                      sx={{
                        py: 1.8,
                        borderRadius: '12px',
                        fontWeight: 700,
                        textTransform: 'none',
                        background: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)',
                        boxShadow: '0 4px 14px rgba(99,102,241,0.3)',
                        '&:hover': {
                          background: 'linear-gradient(135deg, #4f46e5 0%, #4338ca 100%)',
                          boxShadow: '0 6px 20px rgba(99,102,241,0.5)'
                        }
                      }}
                    >
                      Enter High School Academy
                    </Button>
                  </Box>
                </Card>
              </AnimatedBox>
            </Grid>

            {/* 2. Project & Research Card */}
            <Grid item xs={12} md={6}>
              <AnimatedBox
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.7, delay: 0.3 }}
                sx={{ height: '100%' }}
              >
                <Card
                  sx={{
                    height: '100%',
                    background: 'rgba(23, 28, 41, 0.45)',
                    backdropFilter: 'blur(20px)',
                    border: '1px solid rgba(217, 70, 239, 0.15)',
                    borderRadius: '24px',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    p: 2.5,
                    boxShadow: '0 20px 40px rgba(0,0,0,0.4)',
                    transition: 'all 0.4s cubic-bezier(0.16, 1, 0.3, 1)',
                    position: 'relative',
                    overflow: 'hidden',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '3px',
                      background: 'linear-gradient(90deg, #d946ef, #a21caf)',
                    },
                    '&:hover': {
                      transform: 'translateY(-6px)',
                      borderColor: 'rgba(217, 70, 239, 0.4)',
                      boxShadow: '0 25px 50px rgba(217, 70, 239, 0.15)'
                    }
                  }}
                >
                  <CardContent sx={{ p: 3.5 }}>
                    <Box 
                      sx={{ 
                        width: 54, 
                        height: 54, 
                        borderRadius: '14px', 
                        background: 'linear-gradient(135deg, #d946ef 0%, #a21caf 100%)',
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center', 
                        mb: 3,
                        boxShadow: '0 8px 16px rgba(217,70,239,0.3)'
                      }}
                    >
                      <DeveloperBoard sx={{ fontSize: 26, color: '#ffffff' }} />
                    </Box>

                    <Typography variant="h4" sx={{ fontWeight: 800, mb: 1, letterSpacing: '-0.01em' }}>
                      Project & Research
                    </Typography>
                    <Typography variant="subtitle2" sx={{ color: '#f5d0fe', fontWeight: 600, mb: 3, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                      Technical & Engineering Workspace
                    </Typography>

                    <Typography variant="body2" sx={{ color: '#94a3b8', mb: 4, lineHeight: 1.7, fontSize: '0.95rem' }}>
                      A secure developer environment hosting premium project repositories, database design utilities, explainable AI analytics, system documentation tools, and shared engineering boards.
                    </Typography>

                    <Stack spacing={2.5}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <CheckCircleOutline sx={{ color: '#f472b6', fontSize: 20 }} />
                        <Typography variant="body2" sx={{ color: '#cbd5e1', fontWeight: 500 }}>Interactive Technical Project Showcase</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <CheckCircleOutline sx={{ color: '#f472b6', fontSize: 20 }} />
                        <Typography variant="body2" sx={{ color: '#cbd5e1', fontWeight: 500 }}>Comprehensive Knowledge Base Modules</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <CheckCircleOutline sx={{ color: '#f472b6', fontSize: 20 }} />
                        <Typography variant="body2" sx={{ color: '#cbd5e1', fontWeight: 500 }}>Research Article Publishing & Bookmarks</Typography>
                      </Box>
                    </Stack>
                  </CardContent>

                  <Box sx={{ px: 3.5, pb: 3.5, pt: 1.5 }}>
                    <Button
                      variant="contained"
                      fullWidth
                      size="large"
                      endIcon={<ArrowForward />}
                      onClick={openLogin}
                      sx={{
                        py: 1.8,
                        borderRadius: '12px',
                        fontWeight: 700,
                        textTransform: 'none',
                        background: 'linear-gradient(135deg, #d946ef 0%, #c084fc 100%)',
                        boxShadow: '0 4px 14px rgba(217,70,239,0.3)',
                        '&:hover': {
                          background: 'linear-gradient(135deg, #c084fc 0%, #a21caf 100%)',
                          boxShadow: '0 6px 20px rgba(217,70,239,0.5)'
                        }
                      }}
                    >
                      Enter Research Workspace
                    </Button>
                  </Box>
                </Card>
              </AnimatedBox>
            </Grid>

          </Grid>
        </Container>
      </Box>

      {/* 3. SUBSIDIARY ECOSYSTEM SECTION */}
      <Box
        id="insights"
        sx={{
          py: 16,
          background: 'linear-gradient(180deg, rgba(15, 23, 42, 0.4), rgba(8, 11, 19, 1))',
          borderTop: '1px solid rgba(255,255,255,0.04)',
          borderBottom: '1px solid rgba(255,255,255,0.04)',
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Typography variant="subtitle2" sx={{ color: '#a5b4fc', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', mb: 2 }}>
              OUR ECOSYSTEM
            </Typography>
            <Typography variant="h3" sx={{ fontWeight: 800, letterSpacing: '-0.02em', mb: 3 }}>
              Three focused subsidiaries, one connected mission.
            </Typography>
            <Typography variant="body1" sx={{ color: '#94a3b8', maxWidth: '760px', mx: 'auto', lineHeight: 1.7 }}>
              A3 Tech is designed to solve real-world impact across education, health, and financial access by combining digital learning, professional research capability, and practical service infrastructure.
            </Typography>
          </Box>

          <Grid container spacing={4}>
            {subsidiaryHighlights.map((item) => (
              <Grid item xs={12} md={4} key={item.title}>
                <Card
                  sx={{
                    height: '100%',
                    background: 'rgba(15, 23, 42, 0.78)',
                    border: '1px solid rgba(255,255,255,0.06)',
                    borderRadius: '22px',
                    boxShadow: '0 18px 40px rgba(15, 23, 42, 0.25)',
                    transition: 'all 0.25s ease',
                    '&:hover': {
                      transform: 'translateY(-6px)',
                      borderColor: 'rgba(165, 180, 252, 0.35)',
                    }
                  }}
                >
                  <CardContent sx={{ p: 4 }}>
                    <Box
                      sx={{
                        width: 58,
                        height: 58,
                        borderRadius: '16px',
                        background: `linear-gradient(135deg, ${item.accent} 0%, rgba(255,255,255,0.12) 100%)`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mb: 3,
                        boxShadow: `0 12px 20px ${item.accent}33`,
                      }}
                    >
                      {item.icon}
                    </Box>
                    <Typography variant="h5" sx={{ fontWeight: 800, mb: 1 }}>
                      {item.title}
                    </Typography>
                    <Typography variant="subtitle2" sx={{ color: '#cbd5e1', fontWeight: 600, mb: 2 }}>
                      {item.summary}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#94a3b8', lineHeight: 1.7, mb: 3 }}>
                      {item.description}
                    </Typography>
                    <Stack spacing={1.5}>
                      {item.bullets.map((bullet) => (
                        <Box key={bullet} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <CheckCircleOutline sx={{ color: item.accent, fontSize: 18 }} />
                          <Typography variant="body2" sx={{ color: '#dfe7f5', fontWeight: 500 }}>
                            {bullet}
                          </Typography>
                        </Box>
                      ))}
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* 4. PLATFORM CORE FEATURES SECTION */}
      <Box 
        id="features"
        sx={{ 
          py: 16, 
          bgcolor: '#080b13', 
          borderTop: '1px solid rgba(255,255,255,0.03)',
          borderBottom: '1px solid rgba(255,255,255,0.03)' 
        }}
      >
        <Container maxWidth="lg">
          
          <Box sx={{ textAlign: 'center', mb: 10 }}>
            <Typography variant="subtitle2" sx={{ color: '#6366f1', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', mb: 2 }}>
              DIVERSE CAPABILITIES
            </Typography>
            <Typography variant="h3" sx={{ fontWeight: 800, letterSpacing: '-0.02em', mb: 3 }}>
              Features Crafted for Excellence
            </Typography>
            <Typography variant="body1" sx={{ color: '#94a3b8', maxWidth: '600px', mx: 'auto' }}>
              Explore how we bridge high school study preparations with professional software and AI systems engineering.
            </Typography>
          </Box>

          <Grid container spacing={4}>
            
            {/* Feature 1 */}
            <Grid item xs={12} sm={6} md={4}>
              <Box 
                sx={{ 
                  p: 4, 
                  height: '100%',
                  borderRadius: '16px',
                  bgcolor: '#0e1322',
                  border: '1px solid rgba(255,255,255,0.03)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    borderColor: 'rgba(99, 102, 241, 0.25)',
                    transform: 'translateY(-4px)',
                    bgcolor: '#11182c'
                  }
                }}
              >
                <Science sx={{ color: '#6366f1', fontSize: 32, mb: 3 }} />
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5 }}>
                  Virtual STEM Labs
                </Typography>
                <Typography variant="body2" sx={{ color: '#94a3b8', lineHeight: 1.6 }}>
                  Simulate complex Physics and Chemistry experiments safely on the web. Perform titration, circuit layouts, and light refraction simulations with grading feedback.
                </Typography>
              </Box>
            </Grid>

            {/* Feature 2 */}
            <Grid item xs={12} sm={6} md={4}>
              <Box 
                sx={{ 
                  p: 4, 
                  height: '100%',
                  borderRadius: '16px',
                  bgcolor: '#0e1322',
                  border: '1px solid rgba(255,255,255,0.03)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    borderColor: 'rgba(99, 102, 241, 0.25)',
                    transform: 'translateY(-4px)',
                    bgcolor: '#11182c'
                  }
                }}
              >
                <MenuBook sx={{ color: '#6366f1', fontSize: 32, mb: 3 }} />
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5 }}>
                  Language Study Hub
                </Typography>
                <Typography variant="body2" sx={{ color: '#94a3b8', lineHeight: 1.6 }}>
                  Includes an interactive dictionary of over 1000 academic words, 100 common English idioms, structured grammar modules, syllabification checkers, and daily challenge tasks.
                </Typography>
              </Box>
            </Grid>

            {/* Feature 3 */}
            <Grid item xs={12} sm={6} md={4}>
              <Box 
                sx={{ 
                  p: 4, 
                  height: '100%',
                  borderRadius: '16px',
                  bgcolor: '#0e1322',
                  border: '1px solid rgba(255,255,255,0.03)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    borderColor: 'rgba(99, 102, 241, 0.25)',
                    transform: 'translateY(-4px)',
                    bgcolor: '#11182c'
                  }
                }}
              >
                <Psychology sx={{ color: '#6366f1', fontSize: 32, mb: 3 }} />
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5 }}>
                  Exam Preparation Engine
                </Typography>
                <Typography variant="body2" sx={{ color: '#94a3b8', lineHeight: 1.6 }}>
                  Experience real WAEC & JAMB conditions with a fully timed exam dashboard. Analyzes performance indicators down to speed, accuracy, and subject mastery.
                </Typography>
              </Box>
            </Grid>

            {/* Feature 4 */}
            <Grid item xs={12} sm={6} md={4}>
              <Box 
                sx={{ 
                  p: 4, 
                  height: '100%',
                  borderRadius: '16px',
                  bgcolor: '#0e1322',
                  border: '1px solid rgba(255,255,255,0.03)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    borderColor: 'rgba(217, 70, 239, 0.25)',
                    transform: 'translateY(-4px)',
                    bgcolor: '#11182c'
                  }
                }}
              >
                <Analytics sx={{ color: '#d946ef', fontSize: 32, mb: 3 }} />
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5 }}>
                  Research Documentation
                </Typography>
                <Typography variant="body2" sx={{ color: '#94a3b8', lineHeight: 1.6 }}>
                  Deep-dive project write-ups detailing federated learning, explainable neural networks, and transportation logistics with code sandbox snippets and links.
                </Typography>
              </Box>
            </Grid>

            {/* Feature 5 */}
            <Grid item xs={12} sm={6} md={4}>
              <Box 
                sx={{ 
                  p: 4, 
                  height: '100%',
                  borderRadius: '16px',
                  bgcolor: '#0e1322',
                  border: '1px solid rgba(255,255,255,0.03)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    borderColor: 'rgba(217, 70, 239, 0.25)',
                    transform: 'translateY(-4px)',
                    bgcolor: '#11182c'
                  }
                }}
              >
                <Security sx={{ color: '#d946ef', fontSize: 32, mb: 3 }} />
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5 }}>
                  Secure Workspace Access
                </Typography>
                <Typography variant="body2" sx={{ color: '#94a3b8', lineHeight: 1.6 }}>
                  Full control over student credentials, parent tokens, research access permissions, and administrator dashboards to keep academic integrity secure.
                </Typography>
              </Box>
            </Grid>

            {/* Feature 6 */}
            <Grid item xs={12} sm={6} md={4}>
              <Box 
                sx={{ 
                  p: 4, 
                  height: '100%',
                  borderRadius: '16px',
                  bgcolor: '#0e1322',
                  border: '1px solid rgba(255,255,255,0.03)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    borderColor: 'rgba(217, 70, 239, 0.25)',
                    transform: 'translateY(-4px)',
                    bgcolor: '#11182c'
                  }
                }}
              >
                <GroupWork sx={{ color: '#d946ef', fontSize: 32, mb: 3 }} />
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5 }}>
                  Knowledge Collaboratives
                </Typography>
                <Typography variant="body2" sx={{ color: '#94a3b8', lineHeight: 1.6 }}>
                  Dedicated student-teacher-parent feedback channels, collaborative code walkthroughs, bookmark managers, and interactive community study boards.
                </Typography>
              </Box>
            </Grid>

          </Grid>
        </Container>
      </Box>


      {/* 4. PLATFORM METRICS & DYNAMIC TEASER */}
      <Box id="insights" sx={{ py: 16, position: 'relative' }}>
        <Container maxWidth="lg">
          <Grid container spacing={6} alignItems="center">
            
            {/* Left side: Text & Tabs */}
            <Grid item xs={12} md={5}>
              <Typography variant="subtitle2" sx={{ color: '#6366f1', fontWeight: 700, mb: 2, textTransform: 'uppercase', letterSpacing: '0.1em' }}>
                LIVE STATISTICS
              </Typography>
              <Typography variant="h3" sx={{ fontWeight: 800, mb: 3, letterSpacing: '-0.02em' }}>
                Designed for Real Impact
              </Typography>
              <Typography variant="body1" sx={{ color: '#94a3b8', mb: 5, lineHeight: 1.7 }}>
                A3 Tech serves thousands of high school student modules, hosts robust engineering database logs, and helps teachers track progress efficiently.
              </Typography>

              {/* Tab Selector */}
              <Box 
                sx={{ 
                  display: 'flex', 
                  gap: 1.5, 
                  p: 0.8, 
                  borderRadius: '12px', 
                  bgcolor: 'rgba(255,255,255,0.03)',
                  border: '1px solid rgba(255,255,255,0.06)',
                  mb: 4
                }}
              >
                <Button
                  onClick={() => setActiveTab('academy')}
                  sx={{
                    flex: 1,
                    py: 1.2,
                    borderRadius: '8px',
                    textTransform: 'none',
                    fontWeight: 600,
                    bgcolor: activeTab === 'academy' ? '#6366f1' : 'transparent',
                    color: '#ffffff',
                    '&:hover': {
                      bgcolor: activeTab === 'academy' ? '#4f46e5' : 'rgba(255,255,255,0.04)'
                    }
                  }}
                >
                  Academy Portal
                </Button>
                <Button
                  onClick={() => setActiveTab('research')}
                  sx={{
                    flex: 1,
                    py: 1.2,
                    borderRadius: '8px',
                    textTransform: 'none',
                    fontWeight: 600,
                    bgcolor: activeTab === 'research' ? '#ec4899' : 'transparent',
                    color: '#ffffff',
                    '&:hover': {
                      bgcolor: activeTab === 'research' ? '#db2777' : 'rgba(255,255,255,0.04)'
                    }
                  }}
                >
                  Research Portal
                </Button>
              </Box>

              {/* Tab Content stats details */}
              {activeTab === 'academy' ? (
                <Box 
                  component={motion.div}
                  key="academy"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                  sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}
                >
                  <Box sx={{ display: 'flex', gap: 3 }}>
                    <Box>
                      <Typography variant="h4" sx={{ fontWeight: 800, color: '#6366f1' }}>98.7%</Typography>
                      <Typography variant="body2" sx={{ color: '#94a3b8' }}>Mock exam pass rates</Typography>
                    </Box>
                    <Box sx={{ borderLeft: '1px solid rgba(255,255,255,0.08)', pl: 3 }}>
                      <Typography variant="h4" sx={{ fontWeight: 800, color: '#6366f1' }}>1,170+</Typography>
                      <Typography variant="body2" sx={{ color: '#94a3b8' }}>English study hub items</Typography>
                    </Box>
                  </Box>
                  <Typography variant="body2" sx={{ color: '#cbd5e1', fontStyle: 'italic' }}>
                    "The exam engine replicates authentic testing conditions, complete with full subject outlines and practical grading dashboards."
                  </Typography>
                </Box>
              ) : (
                <Box 
                  component={motion.div}
                  key="research"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                  sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}
                >
                  <Box sx={{ display: 'flex', gap: 3 }}>
                    <Box>
                      <Typography variant="h4" sx={{ fontWeight: 800, color: '#ec4899' }}>120+</Typography>
                      <Typography variant="body2" sx={{ color: '#94a3b8' }}>Research system logs</Typography>
                    </Box>
                    <Box sx={{ borderLeft: '1px solid rgba(255,255,255,0.08)', pl: 3 }}>
                      <Typography variant="h4" sx={{ fontWeight: 800, color: '#ec4899' }}>15+</Typography>
                      <Typography variant="body2" sx={{ color: '#94a3b8' }}>Artificial Intelligence topics</Typography>
                    </Box>
                  </Box>
                  <Typography variant="body2" sx={{ color: '#cbd5e1', fontStyle: 'italic' }}>
                    "Access to detailed research, machine learning projects, software structures, and security configurations in a unified portal."
                  </Typography>
                </Box>
              )}
            </Grid>

            {/* Right side: Mock dashboard container */}
            <Grid item xs={12} md={7}>
              <Box 
                sx={{ 
                  borderRadius: '24px', 
                  bgcolor: '#0e1322', 
                  border: '1px solid rgba(255,255,255,0.06)',
                  p: 3,
                  boxShadow: '0 20px 50px rgba(0,0,0,0.5)',
                  position: 'relative'
                }}
              >
                {/* Mock dashboard window bar */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#ef4444' }} />
                    <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#eab308' }} />
                    <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#22c55e' }} />
                  </Box>
                  <Typography variant="caption" sx={{ color: '#475569', letterSpacing: '0.05em' }}>A3_TECH_WORKSPACE.LOG</Typography>
                </Box>

                {/* Simulated dashboard view based on tab */}
                <Box 
                  sx={{ 
                    borderRadius: '12px', 
                    bgcolor: '#090b11', 
                    p: 3, 
                    border: '1px solid rgba(255,255,255,0.03)',
                    minHeight: '260px',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between'
                  }}
                >
                  {activeTab === 'academy' ? (
                    <Box>
                      <Typography variant="subtitle2" sx={{ color: '#6366f1', mb: 2, fontWeight: 700 }}>JAMB/WAEC MOCK EXAM BOARD</Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.8 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid rgba(255,255,255,0.04)', pb: 1 }}>
                          <Typography variant="body2" sx={{ color: '#cbd5e1' }}>Physics Mock Test 1</Typography>
                          <Typography variant="body2" sx={{ color: '#22c55e', fontWeight: 600 }}>Completed (85%)</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid rgba(255,255,255,0.04)', pb: 1 }}>
                          <Typography variant="body2" sx={{ color: '#cbd5e1' }}>Chemistry Labs Simulation</Typography>
                          <Typography variant="body2" sx={{ color: '#22c55e', fontWeight: 600 }}>Graded (A)</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid rgba(255,255,255,0.04)', pb: 1 }}>
                          <Typography variant="body2" sx={{ color: '#cbd5e1' }}>English Grammer Guide 1</Typography>
                          <Typography variant="body2" sx={{ color: '#a5b4fc' }}>Reading (Passage 3)</Typography>
                        </Box>
                      </Box>
                    </Box>
                  ) : (
                    <Box>
                      <Typography variant="subtitle2" sx={{ color: '#ec4899', mb: 2, fontWeight: 700 }}>ENGINEERING RESEARCH SYSTEM</Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.8 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid rgba(255,255,255,0.04)', pb: 1 }}>
                          <Typography variant="body2" sx={{ color: '#cbd5e1' }}>federated-learning-sketching</Typography>
                          <Typography variant="body2" sx={{ color: '#38bdf8' }}>Active Repo</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid rgba(255,255,255,0.04)', pb: 1 }}>
                          <Typography variant="body2" sx={{ color: '#cbd5e1' }}>explainable-ai-neural-networks</Typography>
                          <Typography variant="body2" sx={{ color: '#38bdf8' }}>Verified Documentation</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid rgba(255,255,255,0.04)', pb: 1 }}>
                          <Typography variant="body2" sx={{ color: '#cbd5e1' }}>intelligent-transit-routing</Typography>
                          <Typography variant="body2" sx={{ color: '#e2e8f0' }}>Draft Article</Typography>
                        </Box>
                      </Box>
                    </Box>
                  )}

                  {/* Window Action footer details */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 3, pt: 2, borderTop: '1px solid rgba(255,255,255,0.05)' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CheckCircleOutline sx={{ color: '#22c55e', fontSize: 16 }} />
                      <Typography variant="caption" sx={{ color: '#94a3b8' }}>System online & verified</Typography>
                    </Box>
                    <Typography variant="caption" sx={{ color: '#475569' }}>v1.4.2</Typography>
                  </Box>
                </Box>
              </Box>
            </Grid>

          </Grid>
        </Container>
      </Box>


      {/* 5. HOW IT WORKS / PATHWAY TIMELINE */}
      <Box 
        id="workflow" 
        sx={{ 
          py: 16, 
          bgcolor: '#080b13', 
          borderTop: '1px solid rgba(255,255,255,0.03)' 
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 10 }}>
            <Typography variant="subtitle2" sx={{ color: '#d946ef', fontWeight: 700, mb: 2, textTransform: 'uppercase', letterSpacing: '0.1em' }}>
              HOW IT WORKS
            </Typography>
            <Typography variant="h3" sx={{ fontWeight: 800, letterSpacing: '-0.02em', mb: 3 }}>
              Your Pathway is Seamless
            </Typography>
            <Typography variant="body1" sx={{ color: '#94a3b8', maxWidth: '600px', mx: 'auto' }}>
              Follow these three simple steps to start testing, learning, or reviewing engineering archives.
            </Typography>
          </Box>

          <Grid container spacing={4}>
            
            {/* Step 1 */}
            <Grid item xs={12} md={4}>
              <Box sx={{ p: 4, height: '100%', display: 'flex', flexDirection: 'column', position: 'relative' }}>
                <Box 
                  sx={{ 
                    width: 44, 
                    height: 44, 
                    borderRadius: '50%', 
                    bgcolor: 'rgba(99, 102, 241, 0.1)', 
                    border: '1px solid rgba(99, 102, 241, 0.25)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#818cf8',
                    fontWeight: 700,
                    mb: 3,
                    fontSize: '1.1rem'
                  }}
                >
                  01
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5 }}>
                  Choose Your Destination
                </Typography>
                <Typography variant="body2" sx={{ color: '#94a3b8', lineHeight: 1.6 }}>
                  Select the high school portal for national exam preparation or the research portal for development and documentation tools.
                </Typography>
              </Box>
            </Grid>

            {/* Step 2 */}
            <Grid item xs={12} md={4}>
              <Box sx={{ p: 4, height: '100%', display: 'flex', flexDirection: 'column', position: 'relative' }}>
                <Box 
                  sx={{ 
                    width: 44, 
                    height: 44, 
                    borderRadius: '50%', 
                    bgcolor: 'rgba(217, 70, 239, 0.1)', 
                    border: '1px solid rgba(217, 70, 239, 0.25)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#f472b6',
                    fontWeight: 700,
                    mb: 3,
                    fontSize: '1.1rem'
                  }}
                >
                  02
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5 }}>
                  Authenticate Securely
                </Typography>
                <Typography variant="body2" sx={{ color: '#94a3b8', lineHeight: 1.6 }}>
                  Register or login with OAuth Google integrations or standard email keys. Set up student or researcher permission configurations.
                </Typography>
              </Box>
            </Grid>

            {/* Step 3 */}
            <Grid item xs={12} md={4}>
              <Box sx={{ p: 4, height: '100%', display: 'flex', flexDirection: 'column', position: 'relative' }}>
                <Box 
                  sx={{ 
                    width: 44, 
                    height: 44, 
                    borderRadius: '50%', 
                    bgcolor: 'rgba(99, 102, 241, 0.1)', 
                    border: '1px solid rgba(99, 102, 241, 0.25)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#818cf8',
                    fontWeight: 700,
                    mb: 3,
                    fontSize: '1.1rem'
                  }}
                >
                  03
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5 }}>
                  Simulate & Collaborate
                </Typography>
                <Typography variant="body2" sx={{ color: '#94a3b8', lineHeight: 1.6 }}>
                  Run simulated exams, interact with digital science labs, review engineering blogs, or collaborate via shared bookmark lists.
                </Typography>
              </Box>
            </Grid>

          </Grid>
        </Container>
      </Box>


      {/* 6. DEDICATED LANDING FOOTER SECTION */}
      <Box 
        component="footer"
        sx={{
          bgcolor: '#07090f',
          borderTop: '1px solid rgba(255, 255, 255, 0.05)',
          pt: 12,
          pb: 6,
          color: '#cbd5e1'
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={6} sx={{ mb: 8 }}>
            
            {/* Column 1: Branding */}
            <Grid item xs={12} md={4}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
                <Box 
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: '8px',
                    background: 'linear-gradient(135deg, #6366f1 0%, #d946ef 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <DeveloperBoard sx={{ fontSize: 18, color: '#ffffff' }} />
                </Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#ffffff' }}>
                  A3 Tech
                </Typography>
              </Box>
              <Typography variant="body2" sx={{ color: '#94a3b8', mb: 4, lineHeight: 1.7, maxWidth: '280px' }}>
                Bridging secondary education outlines with cutting-edge software and machine learning engineering archives.
              </Typography>
              <Box sx={{ display: 'flex', gap: 1.5 }}>
                <IconButton sx={{ color: '#94a3b8', bgcolor: 'rgba(255,255,255,0.03)', '&:hover': { color: '#ffffff', bgcolor: 'rgba(255,255,255,0.06)' } }}>
                  <Twitter sx={{ fontSize: 18 }} />
                </IconButton>
                <IconButton sx={{ color: '#94a3b8', bgcolor: 'rgba(255,255,255,0.03)', '&:hover': { color: '#ffffff', bgcolor: 'rgba(255,255,255,0.06)' } }}>
                  <LinkedIn sx={{ fontSize: 18 }} />
                </IconButton>
                <IconButton sx={{ color: '#94a3b8', bgcolor: 'rgba(255,255,255,0.03)', '&:hover': { color: '#ffffff', bgcolor: 'rgba(255,255,255,0.06)' } }}>
                  <GitHub sx={{ fontSize: 18 }} />
                </IconButton>
              </Box>
            </Grid>

            {/* Column 2: Pathways */}
            <Grid item xs={6} sm={4} md={2.5}>
              <Typography variant="subtitle2" sx={{ color: '#ffffff', fontWeight: 700, mb: 3 }}>
                Portals & Pathways
              </Typography>
              <Stack spacing={2}>
                <Typography 
                  variant="body2" 
                  onClick={() => navigate('/high-school/login')}
                  sx={{ color: '#94a3b8', cursor: 'pointer', transition: 'color 0.2s', '&:hover': { color: '#ffffff' } }}
                >
                  High School Academy
                </Typography>
                <Typography 
                  variant="body2" 
                  onClick={openLogin}
                  sx={{ color: '#94a3b8', cursor: 'pointer', transition: 'color 0.2s', '&:hover': { color: '#ffffff' } }}
                >
                  Research Workspace
                </Typography>
                <Typography 
                  variant="body2" 
                  onClick={() => navigate('/school/login')}
                  sx={{ color: '#94a3b8', cursor: 'pointer', transition: 'color 0.2s', '&:hover': { color: '#ffffff' } }}
                >
                  School Management
                </Typography>
                <Typography 
                  variant="body2" 
                  onClick={() => navigate('/parent/login')}
                  sx={{ color: '#94a3b8', cursor: 'pointer', transition: 'color 0.2s', '&:hover': { color: '#ffffff' } }}
                >
                  Parent Tracker
                </Typography>
              </Stack>
            </Grid>

            {/* Column 3: Resources */}
            <Grid item xs={6} sm={4} md={2.5}>
              <Typography variant="subtitle2" sx={{ color: '#ffffff', fontWeight: 700, mb: 3 }}>
                Learning Resources
              </Typography>
              <Stack spacing={2}>
                <Typography 
                  variant="body2" 
                  onClick={() => navigate('/about')}
                  sx={{ color: '#94a3b8', cursor: 'pointer', transition: 'color 0.2s', '&:hover': { color: '#ffffff' } }}
                >
                  About Platform
                </Typography>
                <Typography 
                  variant="body2" 
                  onClick={() => navigate('/faq')}
                  sx={{ color: '#94a3b8', cursor: 'pointer', transition: 'color 0.2s', '&:hover': { color: '#ffffff' } }}
                >
                  General FAQ
                </Typography>
                <Typography 
                  variant="body2" 
                  onClick={() => navigate('/contact')}
                  sx={{ color: '#94a3b8', cursor: 'pointer', transition: 'color 0.2s', '&:hover': { color: '#ffffff' } }}
                >
                  Contact Support
                </Typography>
                <Typography 
                  variant="body2" 
                  onClick={() => navigate('/subscription')}
                  sx={{ color: '#94a3b8', cursor: 'pointer', transition: 'color 0.2s', '&:hover': { color: '#ffffff' } }}
                >
                  Pricing Options
                </Typography>
              </Stack>
            </Grid>

            {/* Column 4: Newsletter teaser */}
            <Grid item xs={12} sm={4} md={3}>
              <Typography variant="subtitle2" sx={{ color: '#ffffff', fontWeight: 700, mb: 3 }}>
                Stay Informed
              </Typography>
              <Typography variant="body2" sx={{ color: '#94a3b8', mb: 3, lineHeight: 1.6 }}>
                Receive notifications when new research articles, science lab exercises, or study materials are published.
              </Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button 
                  variant="outlined"
                  onClick={() => navigate('/contact')}
                  sx={{ 
                    textTransform: 'none', 
                    borderRadius: '8px', 
                    borderColor: 'rgba(255,255,255,0.15)',
                    color: '#ffffff',
                    fontWeight: 600,
                    px: 2.5,
                    py: 1,
                    '&:hover': { borderColor: '#ffffff', bgcolor: 'rgba(255,255,255,0.03)' }
                  }}
                >
                  Subscribe
                </Button>
              </Box>
            </Grid>

          </Grid>

          <Divider sx={{ borderColor: 'rgba(255,255,255,0.06)', mb: 4 }} />

          {/* Bottom Footer Details */}
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
            <Typography variant="caption" sx={{ color: '#64748b' }}>
              &copy; {new Date().getFullYear()} A3 Tech Inc. All rights reserved.
            </Typography>
            <Box sx={{ display: 'flex', gap: 3 }}>
              <Link component={RouterLink} to="/privacy" underline="hover" variant="caption" sx={{ color: '#64748b', '&:hover': { color: '#94a3b8' } }}>Privacy Policy</Link>
              <Link component={RouterLink} to="/terms" underline="hover" variant="caption" sx={{ color: '#64748b', '&:hover': { color: '#94a3b8' } }}>Terms of Service</Link>
            </Box>
          </Box>
        </Container>
      </Box>

    </Box>
  );
};

export default Landing;
